import type { ColumnFiltersState } from '@tanstack/react-table'
import { createCrudApi } from '@/shared/hooks/use-crud-api'
import { apiClient } from '@/shared/lib/api/api-client'
import type { SortModel, PaginationModel, FilterValue } from '@/shared/lib/api/api-types'
import type { BankAccount, BankAccountFilterValues, SetBalanceRequest } from '../types/bank-account'

export const bankAccountsApi = createCrudApi<BankAccount>('Accounts')

// Matches ConnectionStatus enum in TreasuryHub.Common (0=Connected, 1=Disconnected)
export const ConnectionStatus = {
  Connected: 0,
  Disconnected: 1,
} as const
export type ConnectionStatus = (typeof ConnectionStatus)[keyof typeof ConnectionStatus]

// Strongly-typed filter matching AccountFilter.cs on the backend
export interface AccountFilter {
  id?: number
  accountName?: string
  accountNumber?: string
  iban?: string
  description?: string
  ownerName?: string
  currencies?: number[]
  companies?: number[]
  institutions?: number[]
  connectivity?: string[]
  active?: boolean
  exceptions?: boolean
  disconnected?: boolean
  connectionStatus?: ConnectionStatus
}

export interface AccountListRequest {
  filter: AccountFilter
  sorting: SortModel[]
  pagination: PaginationModel
}

export interface AccountListResponse {
  Records: BankAccount[]
  TotalRecords: number
  TotalActives: number
  PageNumber: number
  PageSize: number
  TotalPages: number
}

/**
 * Maps TanStack column filters + tab state → AccountFilter for the backend.
 * Set filters on currency/institution/company send IDs (looked up via filterValues).
 */
export function buildAccountFilter(
  columnFilters: ColumnFiltersState,
  filterValues: BankAccountFilterValues | undefined,
  tab: { exceptions: boolean; disconnected: boolean },
): AccountFilter {
  const filter: AccountFilter = {
    exceptions: tab.exceptions,
    disconnected: tab.disconnected,
  }

  for (const cf of columnFilters) {
    const v = cf.value as FilterValue | undefined
    if (!v) continue

    if (v.filterType === 'text' && v.filter) {
      if (cf.id === 'accountName') filter.accountName = v.filter
      else if (cf.id === 'accountNumber') filter.accountNumber = v.filter
      else if (cf.id === 'iban') filter.iban = v.filter
      else if (cf.id === 'description') filter.description = v.filter
      else if (cf.id === 'ownerName') filter.ownerName = v.filter
    } else if (v.filterType === 'number' && typeof v.filter === 'number') {
      // Backend AccountFilter only supports exact match on Id (no range/compare).
      // Any comparison type from NumberFilter (greaterThan, inRange, etc.) is
      // mapped to an exact match on the value; non-equals operators effectively
      // behave like equals until the backend supports richer predicates.
      if (cf.id === 'id') filter.id = v.filter
    } else if (v.filterType === 'set' && v.values.length > 0) {
      if (cf.id === 'currencyIsoCode') {
        filter.currencies = v.values
          .map((l) => filterValues?.currencies.find((o) => o.Label === l)?.Id as number)
          .filter(Boolean)
      } else if (cf.id === 'institutionName') {
        filter.institutions = v.values
          .map((l) => filterValues?.institutions.find((o) => o.Label === l)?.Id as number)
          .filter(Boolean)
      } else if (cf.id === 'companyName') {
        filter.companies = v.values
          .map((l) => filterValues?.companies.find((o) => o.Label === l)?.Id as number)
          .filter(Boolean)
      } else if (cf.id === 'connectivity') {
        // connectivity: Id === Label (both are display strings)
        filter.connectivity = v.values
          .map((l) => (filterValues?.connectivity.find((o) => o.Label === l)?.Id as string) ?? l)
      } else if (cf.id === 'connectionStatus') {
        // Map to the nullable ConnectionStatus enum on the backend. The
        // `disconnected` boolean flag is only useful for "show disconnected"
        // (it's a no-op when false), so it cannot express "show only connected".
        if (v.values.length === 1) {
          filter.connectionStatus = v.values[0] === 'Connected'
            ? ConnectionStatus.Connected
            : ConnectionStatus.Disconnected
        }
        // length === 0 or 2 → leave unset so the backend returns all statuses.
      } else if (cf.id === 'active') {
        // BooleanFilter stores as { filterType: 'set', values: ['true'|'false'] }
        filter.active = v.values[0] === 'true'
      }
    }
  }

  return filter
}

export function fetchAccountList(req: AccountListRequest) {
  return apiClient.post<AccountListResponse>('/api/Accounts/list', req)
}

export function fetchAccountFilterValues() {
  return apiClient.get<BankAccountFilterValues>('/api/Accounts/getAGGridFilterValues/')
}

export function setAccountBalance(req: SetBalanceRequest) {
  return apiClient.post<void>('/api/Accounts/setAccountBalance', req)
}

export function exportAccounts(ext: 'csv' | 'xlsx', visibleFields: string[]) {
  return apiClient.postBlob(`/api/Accounts/exportData/${ext}`, { visibleFields })
}

export function reconnectOpenBanking(accountId: number) {
  const returnPath = encodeURIComponent('callback/addBank')
  return apiClient.get<OpenBankingLinkResponse>(
    `/api/OpenBankings/accounts/${accountId}/reconnect?returnPath=${returnPath}`,
  )
}

// ── Open Banking linking ──────────────────────────────────

export interface OpenBankingLinkResponse {
  redirectUrl?: string
  url?: string
  Link?: string
}

export function getOpenBankingLink(institutionId: number, companyId: number): Promise<OpenBankingLinkResponse> {
  const returnPath = encodeURIComponent('callback/addBank')
  return apiClient.get<OpenBankingLinkResponse>(
    `/api/OpenBankings/getNewBankLink/${institutionId}/${companyId}?returnPath=${returnPath}`,
  )
}

export function completeOpenBankingLink(ref: string): Promise<void> {
  return apiClient.get<void>(`/api/OpenBankings/returnAddNewBank/${ref}`)
}
